import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MapPin, CheckCircle2, Edit, Trash2, Sparkles, Heart, Send, ExternalLink } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import NeedCard from "@/components/NeedCard";
import JobCard from "@/components/JobCard";
import ListingCard from "@/components/ListingCard";
import EventCard from "@/components/EventCard";
import ShareCard from "@/components/ShareCard";
import ReportButton from "@/components/ReportButton";
import MessageButton from "@/components/MessageButton";
import SaveButton from "@/components/SaveButton";
import AdminRemoveButton from "@/components/AdminRemoveButton";

const INTENT = {
  need: { label: "Someone in the community is looking for…", tag: "I NEED", bg: "bg-[#C05A3D] text-white" },
  have: { label: "Available to the community", tag: "I HAVE", bg: "bg-[#1E3A2F] text-[#DDA74A]" },
  offer: { label: "Offering a service", tag: "I OFFER", bg: "bg-[#DDA74A] text-[#1E3A2F]" },
  know: { label: "Sharing a recommendation", tag: "I KNOW", bg: "bg-[#3B5A6B] text-white" },
};

export default function NeedDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [need, setNeed] = useState(null);
  const [matches, setMatches] = useState(null);
  const [recs, setRecs] = useState([]);
  const [rec, setRec] = useState({ recommended_name: "", contact: "", url: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/needs/${id}`).then((r) => setNeed(r.data)).catch(() => setNeed(false));
    api.get(`/needs/${id}/matches`).then((r) => setMatches(r.data)).catch(() => setMatches({ needs: [], jobs: [], listings: [], events: [] }));
    api.get(`/needs/${id}/recommendations`).then((r) => setRecs(r.data.items)).catch(() => setRecs([]));
  }, [id]);

  if (need === false) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Post not found.</div>;
  if (!need) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Loading…</div>;

  const style = INTENT[need.intent] || INTENT.need;
  const isOwner = user && user.id === need.owner_id;
  const remove = async () => {
    if (!confirm("Delete this post?")) return;
    await api.delete(`/needs/${need.id}`);
    toast.success("Deleted"); nav("/needs");
  };
  const resolve = async () => {
    await api.post(`/needs/${need.id}/resolve`, { resolution_type: "found" });
    toast.success("Marked as resolved");
    api.get(`/needs/${id}`).then((r) => setNeed(r.data));
  };
  const submitRec = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!rec.recommended_name.trim()) return toast.error("Enter who you recommend");
    setBusy(true);
    try {
      const { data } = await api.post(`/needs/${need.id}/recommendations`, rec);
      setRecs((prev) => [data, ...prev]);
      setRec({ recommended_name: "", contact: "", url: "", note: "" });
      setNeed((prev) => ({ ...prev, recommendation_count: (prev?.recommendation_count || 0) + 1 }));
      toast.success("Recommendation added — thank you");
    } catch { toast.error("Could not save recommendation"); }
    setBusy(false);
  };

  return (
    <div>
      <section className="bg-[#1E3A2F] text-[#F7F4EB] relative overflow-hidden">
        <div className="absolute inset-0 abm-kente opacity-[0.06]" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 relative">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#DDA74A] mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${style.bg}`}>{style.tag}</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 max-w-4xl leading-tight">{need.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-[#A3B3AA] text-sm flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {need.location || "Washington"}</span>
            <span>By {need.owner_name || "Member"}</span>
            {need.owner_verified && <VerifiedBadge tier={need.owner_verification_tier} size="sm" />}
            {need.status === "resolved" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#DDA74A] text-[#1E3A2F] text-xs font-medium">
                <CheckCircle2 className="w-3 h-3" /> {need.resolution_type || "Resolved"}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1.4fr_0.6fr] gap-10">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A2F]">Details</h2>
          <p className="mt-4 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{need.description}</p>
          {need.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {need.tags.map((t, i) => <span key={i} className="abm-chip">#{t}</span>)}
            </div>
          )}

          {/* Recommendations */}
          <div className="mt-10">
            <h2 className="font-display text-2xl text-[#1E3A2F] inline-flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#C05A3D]" /> Recommend someone ({recs.length})
            </h2>
            <p className="text-sm text-[#5C5C5C] mt-1">Know a person or business who fits? Drop their name here — they'll thank you.</p>
            <form onSubmit={submitRec} className="mt-4 space-y-3 bg-white border border-[#E2DED0] rounded-2xl p-4 sm:p-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Who</Label>
                  <Input data-testid="rec-name" required value={rec.recommended_name} onChange={(e) => setRec({ ...rec, recommended_name: e.target.value })} placeholder="Business or person's name" />
                </div>
                <div>
                  <Label>Contact (optional)</Label>
                  <Input data-testid="rec-contact" value={rec.contact} onChange={(e) => setRec({ ...rec, contact: e.target.value })} placeholder="Phone, WhatsApp or email" />
                </div>
              </div>
              <div>
                <Label>Link (optional)</Label>
                <Input data-testid="rec-url" value={rec.url} onChange={(e) => setRec({ ...rec, url: e.target.value })} placeholder="https://…" />
              </div>
              <div>
                <Label>Why them</Label>
                <Textarea data-testid="rec-note" rows={2} value={rec.note} onChange={(e) => setRec({ ...rec, note: e.target.value })} placeholder="One sentence about why they're the right fit." />
              </div>
              <Button data-testid="rec-submit" type="submit" disabled={busy} className="abm-btn-primary rounded-full">
                <Send className="w-4 h-4 mr-1" /> {busy ? "Sending…" : "Recommend"}
              </Button>
            </form>

            <div className="mt-5 space-y-3">
              {recs.map((r) => (
                <div key={r.id} className="rounded-xl border border-[#E2DED0] bg-white p-4" data-testid={`recommendation-${r.id}`}>
                  <div className="flex flex-wrap justify-between items-baseline gap-2">
                    <div>
                      <div className="font-display text-lg text-[#1E3A2F] flex items-center gap-2">
                        {r.recommended_name}
                        {r.url && <a href={r.url.startsWith("http") ? r.url : `https://${r.url}`} target="_blank" rel="noreferrer" className="text-[#C05A3D] inline-flex items-center gap-1 text-xs"><ExternalLink className="w-3 h-3" /> link</a>}
                      </div>
                      {r.contact && <div className="text-sm text-[#5C5C5C]">{r.contact}</div>}
                    </div>
                    <div className="text-xs text-[#5C5C5C]">by {r.recommender_name || "someone"}</div>
                  </div>
                  {r.note && <p className="mt-2 text-sm text-[#1A1A1A]">{r.note}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Matches */}
          {matches && (matches.needs?.length || matches.jobs?.length || matches.listings?.length || matches.events?.length) ? (
            <div className="mt-12">
              <h2 className="font-display text-2xl text-[#1E3A2F] inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#DDA74A]" /> Possible matches</h2>
              {matches.needs?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] mb-2">From the community</div>
                  <div className="grid sm:grid-cols-2 gap-4">{matches.needs.map((n) => <NeedCard key={n.id} need={n} />)}</div>
                </div>
              )}
              {matches.jobs?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] mb-2">From healthcare jobs</div>
                  <div className="grid sm:grid-cols-2 gap-4">{matches.jobs.map((j) => <JobCard key={j.id} job={j} />)}</div>
                </div>
              )}
              {matches.listings?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] mb-2">From marketplace</div>
                  <div className="grid sm:grid-cols-2 gap-4">{matches.listings.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
                </div>
              )}
              {matches.events?.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] mb-2">From events</div>
                  <div className="grid sm:grid-cols-2 gap-4">{matches.events.map((e) => <EventCard key={e.id} event={e} />)}</div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="rounded-2xl bg-white border border-[#E2DED0] p-5 space-y-3">
            <ShareCard need={need} />
            {isOwner && need.status !== "resolved" && (
              <Button data-testid="need-resolve" onClick={resolve} className="abm-btn-primary rounded-full h-11 w-full">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as resolved
              </Button>
            )}
            {need.contact_method && (
              <div className="rounded-xl bg-[#F7F4EB] border border-[#E2DED0] p-3 text-sm text-[#1A1A1A]">
                <div className="text-xs uppercase tracking-widest text-[#5C5C5C] mb-1">Contact</div>
                {need.contact_method}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-[#E2DED0]">
              <Stat label="Views" value={need.views} />
              <Stat label="Recs" value={need.recommendation_count} />
              <Stat label="Shares" value={need.share_count} />
            </div>
            {!isOwner && (
              <div className="pt-3 border-t border-[#E2DED0] flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <MessageButton
                    recipientId={need.owner_id}
                    recipientName={need.owner_name || "Poster"}
                    targetType="need"
                    targetId={need.id}
                    targetTitle={need.title}
                    size="sm"
                  />
                  <SaveButton type="need" id={need.id} />
                </div>
                <ReportButton targetType="need" targetId={need.id} targetTitle={need.title} />
              </div>
            )}
            <div className="pt-3 border-t border-[#E2DED0] flex justify-end">
              <AdminRemoveButton type="need" id={need.id} title={need.title} redirectTo="/needs" />
            </div>
          </div>
          {isOwner && (
            <div className="rounded-2xl bg-white border border-[#E2DED0] p-5 space-y-2">
              <Button data-testid="need-edit" onClick={() => nav(`/post/need/${need.id}`)} variant="outline" className="rounded-full w-full">
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button data-testid="need-delete" onClick={remove} variant="destructive" className="rounded-full w-full">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-xl text-[#1E3A2F]">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">{label}</div>
    </div>
  );
}
